/**
 * FaS WebAuthn SDK - Điểm vào chính
 * SDK JavaScript chính thức cho xác thực không mật khẩu
 */

import { startRegistration, startAuthentication } from '@simplewebauthn/browser';

/**
 * Class SDK chính cho tích hợp FaS WebAuthn
 */
class FaSSDK {
  /**
   * Khởi tạo SDK
   * @param {Object} config - Đối tượng cấu hình
   * @param {string} [config.clientId] - Client ID của project bạn (Legacy - Private API)
   * @param {string} [config.projectId] - Project ID cho Public API (Recommended)
   * @param {string} [config.clientSecret] - Client secret (required cho Private API)
   * @param {string} [config.apiBase] - URL cơ sở API (mặc định: production)
   * @param {boolean} [config.useProxy] - Sử dụng backend proxy thay vì direct API calls
   * @param {number} [config.timeout] - Timeout request tính bằng ms (mặc định: 60000)
   */
  constructor(config) {
    // Support both clientId (legacy) and projectId (new public API)
    if (!config.clientId && !config.projectId) {
      throw new Error('Either clientId or projectId is required');
    }

    // Determine if using Public API or Private API
    this.isPublicApi = !!config.projectId;
    
    if (this.isPublicApi) {
      // Public API mode
      this.projectId = config.projectId;
      this.apiBase = config.apiBase || '/api/public';
      this.useProxy = false; // Public API always direct
      console.log('🌐 FaS SDK initialized in Public API mode');
    } else {
      // Private API mode (legacy)
      this.clientId = config.clientId;
      this.clientSecret = config.clientSecret;
      this.useProxy = config.useProxy || false;
      this.apiBase = config.apiBase || 'https://fas-l450.onrender.com/api/webauthn';
      
      // Nếu không dùng proxy thì clientSecret là bắt buộc
      if (!this.useProxy && !this.clientSecret) {
        throw new Error('clientSecret is required when not using proxy mode. Set useProxy: true to use backend proxy.');
      }
      console.log('🔒 FaS SDK initialized in Private API mode');
    }

    this.timeout = config.timeout || 60000;
  }

  /**
   * Thực hiện request API có xác thực
   * @private
   */
  async makeRequest(endpoint, data = {}) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      let headers = {
        'Content-Type': 'application/json'
      };

      // Set authentication headers based on API mode
      if (this.isPublicApi) {
        // Public API - use project ID
        headers['X-Project-ID'] = this.projectId;
        console.log('🌐 Public API request with Project ID:', this.projectId);
      } else {
        // Private API - use client credentials (if not using proxy)
        if (!this.useProxy) {
          headers['X-Client-ID'] = this.clientId;
          headers['X-Client-Secret'] = this.clientSecret;
          console.log('🔒 Private API request with Client ID:', this.clientId);
        }
      }

      // Build URL based on API mode and proxy settings
      let url = `${this.apiBase}${endpoint}`;
      
      if (this.isPublicApi) {
        // Public API - map to public endpoints
        const publicEndpointMap = {
          '/public-register/start': '/register/start',
          '/public-register/finish': '/register/finish', 
          '/public-authenticate/start': '/authenticate/start',
          '/public-authenticate/finish': '/authenticate/finish'
        };
        
        const publicEndpoint = publicEndpointMap[endpoint];
        if (publicEndpoint) {
          url = `${this.apiBase}${publicEndpoint}`;
        }
      } else if (this.useProxy) {
        // Private API with proxy - map endpoints
        const endpointMap = {
          '/public-register/start': '/register/start',
          '/public-register/finish': '/register/finish', 
          '/public-authenticate/start': '/authenticate/start',
          '/public-authenticate/finish': '/authenticate/finish'
        };
        
        const proxyEndpoint = endpointMap[endpoint];
        if (proxyEndpoint) {
          url = `${this.apiBase}${proxyEndpoint}`;
        }
      }

      console.log('📡 Making request to:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  /**
   * Đăng ký passkey mới cho người dùng
   * @param {string} email - Địa chỉ email người dùng
   * @param {string} [fullname] - Tên đầy đủ của người dùng
   * @returns {Promise<Object>} Kết quả đăng ký với thông tin user và JWT token
   */
  async registerPasskey(email, fullname) {
    if (!email) {
      throw new Error('Email is required');
    }

    try {
      console.log('🔑 Bắt đầu đăng ký passkey cho:', email);

      // Bước 1: Lấy tùy chọn đăng ký
      const options = await this.makeRequest('/public-register/start', {
        email,
        fullname: fullname || email
      });

      console.log('📋 Đã nhận tùy chọn đăng ký');

      // Bước 2: Bắt đầu đăng ký WebAuthn
      const credential = await startRegistration(options);

      console.log('✅ Hoàn tất đăng ký WebAuthn');

      // Bước 3: Xác minh đăng ký
      const result = await this.makeRequest('/public-register/finish', {
        email,
        credential
      });

      console.log('🎉 Xác minh đăng ký thành công');

      // Tự động lưu token
      if (result.token) {
        this.setAuthToken(result.token);
      }

      return {
        success: true,
        user: result.user,
        token: result.token
      };

    } catch (error) {
      console.error('❌ Đăng ký thất bại:', error);
      throw this.handleWebAuthnError(error);
    }
  }

  /**
   * Xác thực bằng passkey đã có
   * @param {string} email - Địa chỉ email người dùng
   * @returns {Promise<Object>} Kết quả xác thực với thông tin user và JWT token
   */
  async authenticatePasskey(email) {
    if (!email) {
      throw new Error('Email is required');
    }

    try {
      console.log('🔐 Starting passkey authentication for:', email);

      // Step 1: Get authentication options
      const options = await this.makeRequest('/public-authenticate/start', {
        email
      });

      console.log('📋 Authentication options received');

      // Step 2: Start WebAuthn authentication
      const credential = await startAuthentication(options);

      console.log('✅ WebAuthn authentication completed');

      // Step 3: Verify authentication
      const result = await this.makeRequest('/public-authenticate/finish', {
        email,
        credential
      });

      console.log('🎉 Authentication verified successfully');

      // Store token automatically
      if (result.token) {
        this.setAuthToken(result.token);
      }

      return {
        success: true,
        user: result.user,
        token: result.token
      };

    } catch (error) {
      console.error('❌ Authentication failed:', error);
      throw this.handleWebAuthnError(error);
    }
  }

  /**
   * Passwordless authentication (no email required)
   * Uses resident keys to identify user automatically
   * @returns {Promise<Object>} Authentication result
   */
  async passwordlessLogin() {
    try {
      console.log('🚀 Starting passwordless authentication');

      // Step 1: Get authentication options (no email)
      const options = await this.makeRequest('/public-authenticate/start', {
        email: null
      });

      console.log('📋 Passwordless options received');

      // Step 2: Start WebAuthn authentication
      const credential = await startAuthentication(options);

      console.log('✅ Passwordless authentication completed');

      // Step 3: Verify authentication
      const result = await this.makeRequest('/public-authenticate/finish', {
        email: null,
        credential
      });

      console.log('🎉 Passwordless authentication verified');

      // Store token automatically
      if (result.token) {
        this.setAuthToken(result.token);
      }

      return {
        success: true,
        user: result.user,
        token: result.token
      };

    } catch (error) {
      console.error('❌ Passwordless authentication failed:', error);
      throw this.handleWebAuthnError(error);
    }
  }

  /**
   * Get stored authentication token
   * @returns {string|null} JWT token or null if not found
   */
  getAuthToken() {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('fas_auth_token');
    }
    return null;
  }

  /**
   * Set authentication token
   * @param {string} token - JWT token to store
   */
  setAuthToken(token) {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('fas_auth_token', token);
    }
  }

  /**
   * Clear authentication token (logout)
   */
  logout() {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('fas_auth_token');
    }
  }

  /**
   * Check if user is currently authenticated
   * @returns {boolean} True if user has valid token
   */
  isAuthenticated() {
    const token = this.getAuthToken();
    if (!token) return false;

    try {
      // Simple JWT expiry check (without signature verification)
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  /**
   * Check if WebAuthn is supported in current browser
   * @returns {boolean} True if WebAuthn is supported
   */
  static isWebAuthnSupported() {
    return !!(
      typeof window !== 'undefined' &&
      window.navigator &&
      window.navigator.credentials &&
      window.navigator.credentials.create
    );
  }

  /**
   * Get detailed browser support information
   * @returns {Object} Support details
   */
  static getBrowserSupport() {
    const support = {
      webauthn: FaSSDK.isWebAuthnSupported(),
      conditionalUI: false,
      residentKeys: false,
      userVerification: false
    };

    if (support.webauthn && typeof window !== 'undefined') {
      // Check for conditional UI support (autofill)
      support.conditionalUI = window.PublicKeyCredential?.isConditionalMediationAvailable !== undefined;
      
      // These features are usually supported if WebAuthn is supported
      support.residentKeys = true;
      support.userVerification = true;
    }

    return support;
  }

  /**
   * Handle WebAuthn errors with user-friendly messages
   * @private
   */
  handleWebAuthnError(error) {
    const errorMap = {
      'NotAllowedError': 'User cancelled the operation or timeout occurred',
      'NotSupportedError': 'WebAuthn is not supported on this device/browser',
      'InvalidStateError': 'A passkey already exists for this account on this device',
      'SecurityError': 'Security error - ensure you are using HTTPS',
      'UnknownError': 'An unknown error occurred during authentication',
      'AbortError': 'Operation was aborted by user or system',
      'ConstraintError': 'Constraint error occurred',
      'NotReadableError': 'Authenticator data is not readable',
      'DataError': 'Data provided is invalid'
    };

    const userMessage = errorMap[error.name] || error.message || 'Unknown authentication error';
    
    const friendlyError = new Error(userMessage);
    friendlyError.originalError = error;
    friendlyError.code = error.name || 'UnknownError';
    
    return friendlyError;
  }
}

// Export for different module systems
export default FaSSDK;

// CommonJS compatibility
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FaSSDK;
}

// Global browser compatibility
if (typeof window !== 'undefined') {
  window.FaSSDK = FaSSDK;
} 